import { useEffect, useRef } from 'react'
import { APP_NAME, APP_VERSION, SUGGESTED_PROMPTS } from '../config'
import { useChat } from '../hooks/useChat'
import { Composer } from './Composer'
import { Markdown } from './Markdown'
import { MessageBubble } from './MessageBubble'

export function Chat({ chat }: { chat: ReturnType<typeof useChat> }) {
  const { messages, streamingText, suggestions, status, error, send, retry } = chat
  const bottom = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages, streamingText])

  const busy = status === 'thinking' || status === 'connecting'
  const isEmpty = messages.length === 0 && !streamingText

  const prompts = suggestions.length > 0 ? suggestions : isEmpty ? SUGGESTED_PROMPTS : []

  return (
    <main className="chat">
      <div className="chat__scroll">
        <div className="chat__thread">
          {isEmpty && status !== 'error' && (
            <section className="empty">
              <h1 className="empty__title">How can {APP_NAME} help?</h1>
              <p className="empty__body">
                Ask about contract language, risk exposure, notice and schedule requirements, or how
                a clause compares to Brinkmann&rsquo;s standard position.
              </p>
            </section>
          )}

          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}

          {streamingText && (
            <article className="message message--agent">
              <div className="message__meta">
                <span className="message__author">{APP_NAME}</span>
              </div>
              <div className="message__body">
                <Markdown text={streamingText} />
              </div>
            </article>
          )}

          {status === 'thinking' && !streamingText && (
            <div className="typing" role="status" aria-live="polite">
              <span />
              <span />
              <span />
            </div>
          )}

          {error && (
            <div className="alert" role="alert">
              <p>{error}</p>
              <button type="button" className="btn btn--ghost" onClick={() => void retry()}>
                Try again
              </button>
            </div>
          )}

          <div ref={bottom} />
        </div>
      </div>

      <div className="chat__footer">
        {prompts.length > 0 && (
          <div className="prompts">
            {prompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="prompt"
                onClick={() => void send(prompt)}
                disabled={busy}
              >
                {prompt}
              </button>
            ))}
          </div>
        )}

        <Composer onSend={(text, documents) => void send(text, documents)} disabled={busy} busy={busy} />

        <p className="disclaimer">
          {APP_NAME} can make mistakes. Verify contract language and legal conclusions before
          relying on them.
        </p>

        <p className="version">{APP_VERSION}</p>
      </div>
    </main>
  )
}
